import { ComponentRef, ElementRef, HostListener, Injectable, Type } from '@angular/core';
import { AbstractService } from './abstract-service';
import { SolPopoverAlignment } from '../components/popover/sol-popover/sol-popover.state';
import { SolPopoverMenuContentComponent } from '../components/popover/sol-popover-menu-content/sol-popover-menu-content.component';
import { SolMenuGroup } from '../components/popover/sol-popover-menu-content/sol-popover-menu-content.state';
import { DateTools } from '../tools/date-tools';
import { ObjectTools } from '../tools/object-tools';

export interface SolPopover<T> {
  id: string;
  loadComponent: Promise<Type<T>>;
  configurer: (componentRef: ComponentRef<T>, id: string) => void;
  options: SolPopoverOptions;
  followsAnchor: boolean;
}

export interface SolPopoverOptions {
  clickOutside: () => void;
  afterClose: () => void;
  bounds: SolRectangle;
  align: SolPopoverAlignment;
  error: boolean;
  width: SolPopoverWidth;
  fadeIn: boolean;
  closeOnHoverOut: boolean;
  boundsEvent: MouseEvent | undefined;
  boundsRef: ElementRef<HTMLElement> | undefined;
  boundsSnapToParent: boolean;
  gapX: number;
  gapY: number;
  clickInsideRefs: ElementRef<HTMLElement>[];
}

export interface SolPopoverRef<T> {
  id: string;
  close: () => void;
  instance: () => T | undefined;
}

export interface PopoversState {
  popovers: SolPopover<any>[];
  lastClosed: Date | undefined;
}

export interface SolRectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * The width strategy of the popover relative to the parent bounds:
 * - auto: popover hugs popover content
 * - parent: popover width is exactly parent width
 * - parent-grow: popover hugs popover content and grows beyond parent width (min = parent width)
 */
export type SolPopoverWidth = 'auto' | 'parent' | 'parent-grow';

/**
 * Service for showing and managing popovers, as well as popover menus.
 */
@Injectable({
  providedIn: 'root',
})
export class PopoverService extends AbstractService<PopoversState> {
  private counter = 1;

  // Cache
  private static instances: Map<string, any> = new Map<string, any>();
  private clientX: number = 0;
  private clientY: number = 0;

  constructor() {
    super({
      popovers: [],
      lastClosed: undefined,
    });
  }

  /**
   * Shows a popover with the given component type and configurer. The options consist of:
   * - clickOutside: The function to call when the user clicks outside the popover.
   * - bounds, boundsEvent, boundsRef: The parent bounds to take into account where to show the
   *   popover.
   * - align: The alignment of the popover relative to the parent bounds.
   * - grow: Whether to grow the popover to fit the parent bounds (width).
   * - fadeIn: Whether to fade in the popover.
   * - error: Whether to show the popover in error mode.
   * - closeOnHoverOut: Whether to self-close the popover shortly after the pointer stops hovering
   *   both the parent (boundsRef, or the boundsEvent target when snapping to it) and the popover
   *   itself.
   */
  public show<T>(
    componentType: Type<T> | Promise<Type<T>>,
    configurer: (componentRef: ComponentRef<T>, id: string) => void,
    options: Partial<SolPopoverOptions> = {},
  ): SolPopoverRef<T> {
    const id = 'popover' + this.counter++;
    this.update(state => {
      const isFirst = this.isFirst(state);
      const finalOptions = this.createOptions(id, options, isFirst);
      const loadComponent = componentType instanceof Promise ? componentType : Promise.resolve(componentType);
      const popover: SolPopover<T> = {
        id,
        loadComponent,
        configurer,
        options: finalOptions,
        followsAnchor: options.bounds === undefined && finalOptions.boundsRef !== undefined,
      };
      return {
        popovers: [...state.popovers, popover],
      };
    });
    return {
      id: id,
      close: () => this.close(id),
      instance: () => PopoverService.instances.get(id) as T,
    };
  }

  /**
   * Shows a popover menu (component: SolPopoverMenuContentComponent) for the given menu and
   * onAction callback function.
   */
  public showMenu(
    menu: SolMenuGroup[],
    onAction: (actionName: string) => boolean,
    options: Partial<SolPopoverOptions> = {},
  ): SolPopoverRef<SolPopoverMenuContentComponent> {
    return this.show<SolPopoverMenuContentComponent>(
      SolPopoverMenuContentComponent,
      (componentRef: ComponentRef<SolPopoverMenuContentComponent>, id: string) => {
        componentRef.instance.menu = menu;
        componentRef.instance.shortcutsEnabled = true;
        const selectedItem = menu.flatMap(g => g.items).find(i => i.selected);
        if (selectedItem) {
          componentRef.instance.currentId = selectedItem.id;
        }
        componentRef.instance.onAction = name => {
          const canClose = onAction(name);
          if (canClose) {
            this.close(id);
          }
          return canClose;
        };
        componentRef.instance.close = () => this.close(id);
      },
      {
        ...options,
      },
    );
  }

  /**
   * Closes the popover with the given id.
   */
  public close(id: string): void {
    // Popover found?
    const state = this.state;
    const popover = state.popovers.find(popover => popover.id === id);
    if (!popover) {
      return;
    }

    // Do close (delete)
    this.update(state => ({
      popovers: state.popovers.filter(popover => popover.id !== id),
      lastClosed: new Date(),
    }));

    // Clean up instances
    PopoverService.instances.delete(id);
    PopoverService.instances.forEach((instance, candidateId) => {
      if (!state.popovers.find(popover => popover.id === candidateId)) {
        PopoverService.instances.delete(candidateId);
      }
    });

    // Run after close
    popover.options.afterClose();
  }

  /**
   * Closes all popovers.
   */
  public closeAll(): void {
    this.state.popovers.forEach(popover => this.close(popover.id));
  }

  /**
   * Re-measures every popover anchored to an element, so an open popover keeps following its anchor
   * while the page scrolls or resizes. Popovers placed at the pointer stay where they were opened.
   */
  public reposition(): void {
    if (this.state.popovers.length === 0) {
      return;
    }
    this.update(state => ({
      popovers: state.popovers.map(popover => PopoverService.toRepositioned(popover)),
    }));
  }

  /**
   * Perform a click outside popover (which will trigger the clickOutside callback if it is the top-level popover).
   */
  public clickOutside(id: string): void {
    // Nothing shown
    const state = this.state;
    const popovers = state.popovers;
    if (popovers.length === 0) {
      return;
    }

    // Not last popover
    const lastPopover = popovers[popovers.length - 1];
    if (lastPopover.id !== id) {
      return;
    }

    // Trigger click outside
    lastPopover.options.clickOutside();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(e: MouseEvent) {
    this.clientX = e.clientX;
    this.clientY = e.clientY;
  }

  public static registerInstance<T>(id: string, instance: T): void {
    PopoverService.instances.set(id, instance);
  }

  private isFirst(state: PopoversState): boolean {
    // Existing popovers?
    if (state.popovers.length > 0) {
      return false;
    }

    // No previously closed popover
    if (!state.lastClosed) {
      return true;
    }

    // Instantly reopened popover?
    const diff = DateTools.diff(DateTools.now(), state.lastClosed);
    return diff > 100;
  }

  private createOptions(id: string, options: Partial<SolPopoverOptions>, isFirst: boolean): SolPopoverOptions {
    // Calculate bounds
    let bounds: SolRectangle;
    if (options.boundsEvent) {
      const snapToParent = options.boundsSnapToParent ?? true;
      bounds = snapToParent
        ? PopoverService.toBounds(options.boundsEvent.target as HTMLElement)
        : {
            x: options.boundsEvent.clientX,
            y: options.boundsEvent.clientY,
            width: 0,
            height: 0,
          };
    } else if (options.boundsRef) {
      bounds = PopoverService.toBounds(options.boundsRef.nativeElement);
    } else {
      bounds = {
        x: this.clientX,
        y: this.clientY,
        width: 0,
        height: 0,
      };
    }

    // Create options
    return {
      clickOutside: () => this.close(id),
      afterClose: () => {},
      bounds,
      align: SolPopoverAlignment.bottomLeft,
      error: false,
      width: 'parent-grow',
      gapX: 0,
      gapY: 4,
      fadeIn: isFirst,
      closeOnHoverOut: false,
      ...options,
      clickInsideRefs: this.createClickInsideRefs(options),
      boundsEvent: undefined,
      boundsSnapToParent: options.boundsSnapToParent ?? true,
      boundsRef: this.createBoundsRef(options),
    };
  }

  private createBoundsRef(options: Partial<SolPopoverOptions>): ElementRef<HTMLElement> | undefined {
    if (options.boundsRef) {
      return options.boundsRef;
    }
    const snapToParent = options.boundsSnapToParent ?? true;
    if (options.boundsEvent && snapToParent) {
      return new ElementRef(options.boundsEvent.target as HTMLElement);
    }
    return undefined;
  }

  private createClickInsideRefs(options: Partial<SolPopoverOptions>): ElementRef<HTMLElement>[] {
    const refs = options.clickInsideRefs ?? [];
    if (!options.boundsRef) {
      return refs;
    }
    return [options.boundsRef, ...refs];
  }

  private static toRepositioned<T>(popover: SolPopover<T>): SolPopover<T> {
    const boundsRef = popover.options.boundsRef;
    if (!popover.followsAnchor || !boundsRef || !PopoverService.isRendered(boundsRef.nativeElement)) {
      return popover;
    }
    const bounds = PopoverService.toBounds(boundsRef.nativeElement);
    if (ObjectTools.equals(bounds, popover.options.bounds)) {
      return popover;
    }
    return { ...popover, options: { ...popover.options, bounds } };
  }

  private static isRendered(element: HTMLElement): boolean {
    return element.getClientRects().length > 0;
  }

  private static toBounds(element: HTMLElement): SolRectangle {
    const rect = element.getBoundingClientRect();
    return { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
  }
}
